const TABLET_QUERY = '(min-width: 768px)'
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const STICKY_BOTTOM_OFFSET = 35
const REVEAL_TIMEOUT = 1500

class RedesignHomeHero {
  constructor (el) {
    this.el = el
    this.items = []
    this.frame = null
    this.tabletQuery = window.matchMedia(TABLET_QUERY)
    this.motionQuery = window.matchMedia(REDUCED_MOTION_QUERY)

    this.update = this.update.bind(this)
    this.requestUpdate = this.requestUpdate.bind(this)
    this.handleModeChange = this.handleModeChange.bind(this)

    this.init()
  }

  init () {
    this.observeSingleImageContent()

    if (!this.isStickyLayout()) return

    this.items = Array.from(this.el.querySelectorAll(
      '.redesign-home-hero__panel .redesign-home-hero__content > .redesign-home-hero__content-inner'
    )).map((inner) => ({
      inner,
      actions: inner.querySelector('.redesign-home-hero__actions')
    })).filter((item) => item.actions)

    if (!this.items.length) return

    window.addEventListener('scroll', this.requestUpdate, { passive: true })
    window.addEventListener('resize', this.requestUpdate)
    this.addMediaListener(this.tabletQuery)
    this.addMediaListener(this.motionQuery)
    this.requestUpdate()
  }

  // Single Image centers its sticky right content in the viewport; CSS needs
  // the content height for that, so expose it as a custom property.
  observeSingleImageContent () {
    if (!this.el.classList.contains('redesign-home-hero--single-image')) return
    if (typeof window.ResizeObserver !== 'function') return

    const content = this.el.querySelector(
      '.redesign-home-hero__panel--right .redesign-home-hero__content'
    )
    if (!content) return

    const observer = new window.ResizeObserver(([entry]) => {
      const height = entry.borderBoxSize && entry.borderBoxSize[0]
        ? entry.borderBoxSize[0].blockSize
        : content.offsetHeight

      content.style.setProperty('--hero-sticky-content-h', `${height}px`)
    })

    observer.observe(content)
    this.revealStickyContent(content)
  }

  // The sticky offset depends on header vars set by other modules at different
  // moments; the header re-measures itself on window load. Keep the content
  // hidden until then, so it appears once in its final spot instead of
  // jumping. CSS reveals it anyway after 2s.
  revealStickyContent (content) {
    const rootStyle = document.documentElement.style
    const hasAnnouncement = Boolean(document.querySelector('[data-module="header-announcement"]'))
    const startedAt = window.performance.now()

    const isReady = () => (
      document.readyState === 'complete' &&
      rootStyle.getPropertyValue('--redesign-header-current-height') !== '' &&
      (!hasAnnouncement || rootStyle.getPropertyValue('--redesign-announcement-height') !== '')
    )

    const check = () => {
      if (!isReady() && window.performance.now() - startedAt < REVEAL_TIMEOUT) {
        window.requestAnimationFrame(check)
        return
      }

      // Two frames: let the new offsets and content height reach layout first.
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
        content.classList.add('redesign-home-hero__content--sticky-ready')
      }))
    }

    check()
  }

  isStickyLayout () {
    return this.el.classList.contains('redesign-home-hero--text-on-image') ||
      this.el.classList.contains('redesign-home-hero--buttons-on-image')
  }

  addMediaListener (query) {
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', this.handleModeChange)
      return
    }

    if (typeof query.addListener === 'function') {
      query.addListener(this.handleModeChange)
    }
  }

  handleModeChange () {
    this.resetOffsets()
    this.requestUpdate()
  }

  requestUpdate () {
    if (this.frame) return

    this.frame = window.requestAnimationFrame(this.update)
  }

  update () {
    this.frame = null

    if (!this.tabletQuery.matches || this.motionQuery.matches) {
      this.resetOffsets()
      return
    }

    const stickyBottom = window.innerHeight - STICKY_BOTTOM_OFFSET
    this.resetOffsets()

    const metrics = this.items.map(({ inner, actions }) => ({
      inner,
      actionsBottom: actions.getBoundingClientRect().bottom
    }))

    const sharedBottom = metrics.reduce((bottom, metric) => (
      Math.min(bottom, metric.actionsBottom)
    ), stickyBottom)

    for (const metric of metrics) {
      this.updateItem(metric, sharedBottom)
    }
  }

  // Shrink the inner from the bottom instead of moving it, so the text block
  // keeps the vertical position its Content Position setting asks for.
  updateItem ({ inner, actionsBottom }, targetBottom) {
    const offset = actionsBottom > targetBottom
      ? actionsBottom - targetBottom
      : 0

    inner.style.paddingBottom = offset ? `${offset.toFixed(2)}px` : ''
  }

  resetOffsets () {
    for (const item of this.items) {
      item.inner.style.paddingBottom = ''
    }
  }
}

export default (el) => new RedesignHomeHero(el)
